import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles, type Article } from "@/mocks/articles";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find((a) => a.id === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Organization", name: article.author },
    datePublished: article.date,
    image: article.cover,
    publisher: {
      "@type": "Organization",
      name: "蘑菇视频",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.moguvideo.com" },
      { "@type": "ListItem", position: 2, name: "文章资讯", item: "https://www.moguvideo.com/articles" },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 蘑菇视频`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},蘑菇视频,视频播放器,文章`}
        canonicalUrl={`https://www.moguvideo.com/article/${article.id}`}
        ogType="article"
        ogImage={article.cover}
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: "文章资讯", path: "/articles" }, { label: article.title }]} />

        <article className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-4 leading-snug" style={{ fontFamily: "var(--font-heading)" }}>
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mb-6">
                <span className="text-xs text-foreground-500">作者：{article.author}</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/articles"
                  className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                  返回文章列表
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}